import { Injectable } from '@angular/core';
import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { FunnelSampleContentService } from 'shared/src/services/funnel-sample-content.service';
import {
  BookId,
  fictionInterestBookIdsMap,
  interleaveBookIds,
  motivationBookIdMap,
  nonFictionInterestBookIdsMap,
} from '../../../../funnel/src/app/data/books';
import {
  FictionInterestId,
  highlightText,
  MotivationId,
  NonFictionInterestId,
} from '../../../../funnel/src/app/data/quiz';
import { FunnelFlowService } from '../../../../funnel/src/app/services/funnel-flow.service';
import { KindleFunnelFlowService } from './kindle-funnel-flow.service';
import {
  PaywallAudioTrackSection,
  PaywallFaq,
  PaywallFeature,
  PaywallService,
} from '../../../../funnel/src/app/services/paywall.service';

declare const $localize: any;

const defaultKindleFictionBookIds: readonly BookId[] = [
  'projectHailMary',
  'theSevenHusbandsOfEvelynHugo',
];
const defaultKindleNonFictionBookIds: readonly BookId[] = ['atomicHabits', 'thePsychologyOfMoney'];
const maxKindlePaywallSamples = 8;
const maxKindlePaywallFictionSamples = 4;

@Injectable({
  providedIn: 'root',
})
export class KindlePaywallService extends PaywallService {
  constructor(
    protected override flowService: FunnelFlowService,
    protected sampleContentService: FunnelSampleContentService
  ) {
    super(flowService);
  }

  // The main funnel's paywall copy for visitors the kindle flow sent down the main one.
  private isOnMainFlow(): boolean {
    return this.flowService instanceof KindleFunnelFlowService && this.flowService.isOnMainFlow();
  }

  protected getDefaultFictionBookIds(): readonly BookId[] {
    return defaultKindleFictionBookIds;
  }

  protected getDefaultNonFictionBookIds(): readonly BookId[] {
    return defaultKindleNonFictionBookIds;
  }

  override getTitle(): string {
    if (this.isOnMainFlow()) {
      return super.getTitle();
    }

    return (
      $localize`:@@kindlePaywallTitlePrefix:Your ` +
      highlightText($localize`:@@kindlePaywallTitleHighlight:Kindle listening plan`) +
      $localize`:@@kindlePaywallTitleSuffix: is ready`
    );
  }

  override getContextualTextBundle(): { title: string; beforeText: string; afterText: string } {
    if (this.isOnMainFlow()) {
      return super.getContextualTextBundle();
    }

    return {
      title: $localize`:@@kindleContextTitle:Reading Flow`,
      beforeText: $localize`:@@kindleContextBefore:Books waiting unread`,
      afterText: $localize`:@@kindleContextAfter:Chapters finished in spare moments`,
    };
  }

  override getReviews(): UserReview[] {
    if (this.isOnMainFlow()) {
      return super.getReviews();
    }

    return [
      {
        name: $localize`:@@kindleReviewName1:Laura Bennett`,
        age: 41,
        message: $localize`:@@kindleReviewMessage1:I had so many books sitting half-finished because evenings were the only time I could read. Now I listen while walking and cooking, and I finally feel connected to my reading list again.`,
        avatarUrl: 'assets/img/avatars/w3.jpg',
        rate: 4.9,
      },
      {
        name: $localize`:@@kindleReviewName2:Marcus Reed`,
        age: 36,
        message: $localize`:@@kindleReviewMessage2:The biggest surprise was how natural long chapters feel as audio. I use Peech for books I already planned to read, and it turns small gaps in the day into real progress.`,
        avatarUrl: 'assets/img/avatars/m2.jpg',
        rate: 4.8,
      },
    ];
  }

  override getPlanCta(): string {
    if (this.isOnMainFlow()) {
      return super.getPlanCta();
    }

    return $localize`:@@kindlePaywallPlanCta:Start your plan while your reader discount is active`;
  }

  override getHighlights(): string[] {
    if (this.isOnMainFlow()) {
      return super.getHighlights();
    }

    return [
      $localize`:@@kindlePaywallHighlight1:Turn long chapters into natural audio`,
      $localize`:@@kindlePaywallHighlight2:Listen during walks, chores, commutes, and screen-free breaks`,
      $localize`:@@kindlePaywallHighlight3:Voices tuned for long-form books, not robotic snippets`,
      $localize`:@@kindlePaywallHighlight4:Speed control for finishing more chapters`,
      $localize`:@@kindlePaywallHighlight5:A reading routine built around books you already meant to finish`,
    ];
  }

  override getFeatures(): PaywallFeature[] {
    if (this.isOnMainFlow()) {
      return super.getFeatures();
    }

    return [
      {
        emoji: '📚',
        title: $localize`:@@kindleFeatureLibraryTitle:Convert your Kindle library into audiobooks`,
        description: $localize`:@@kindleFeatureLibraryDesc:Turn your books into natural-sounding audio in just a few taps.`,
      },
      {
        emoji: '📖',
        title: $localize`:@@kindleFeature1Title:Turn book text into audio`,
        description: $localize`:@@kindleFeature1Desc:Use Peech with supported ebooks, documents, links, and readable text you provide.`,
      },
      {
        emoji: '🎧',
        title: $localize`:@@kindleFeature2Title:Natural voices for long chapters`,
        description: $localize`:@@kindleFeature2Desc:Clear narration that stays comfortable through fiction, non-fiction, and dense passages.`,
      },
      {
        emoji: '🚶',
        title: $localize`:@@kindleFeature3Title:Read while your hands are busy`,
        description: $localize`:@@kindleFeature3Desc:Keep books moving during walks, chores, workouts, errands, and commutes.`,
      },
      {
        emoji: '👀',
        title: $localize`:@@kindleFeature4Title:Give your eyes a break`,
        description: $localize`:@@kindleFeature4Desc:Switch from screen time to listening when reading at night feels tiring.`,
      },
      {
        emoji: '⏩',
        title: $localize`:@@kindleFeature5Title:Control the pace`,
        description: $localize`:@@kindleFeature5Desc:Slow down for complex chapters or speed up familiar sections without losing the thread.`,
      },
      {
        emoji: '✅',
        title: $localize`:@@kindleFeature6Title:Built for finishing`,
        description: $localize`:@@kindleFeature6Desc:Turn small pockets of time into real progress on books waiting in your library.`,
      },
    ];
  }

  override getAudioTrackSections(): PaywallAudioTrackSection[] {
    if (this.isOnMainFlow()) {
      return super.getAudioTrackSections();
    }

    const fictionInterestIds = this.flowService.getFictionInterestIds();
    const nonFictionInterestIds = this.flowService.getNonFictionInterestIds();
    const motivationIds = this.flowService.getMotivationIds();
    const prioritizedFictionBookIds = this.getBookIdsForInterests(
      fictionInterestIds,
      fictionInterestBookIdsMap,
      this.getDefaultFictionBookIds()
    );
    const fictionBookIds = this.takeUniqueBookIds(
      prioritizedFictionBookIds,
      maxKindlePaywallFictionSamples
    );
    const motivationBookIds = this.getMotivationBookIds(motivationIds);
    const nonFictionCandidateBookIds = this.getBookIdsForInterests(
      nonFictionInterestIds,
      nonFictionInterestBookIdsMap,
      this.getDefaultNonFictionBookIds()
    );
    const motivationReserve = Math.min(
      motivationBookIds.length,
      this.getRemainingSampleLimit(fictionBookIds.length) > 0 ? 1 : 0
    );
    const nonFictionInterestBookIds = this.takeUniqueBookIds(
      nonFictionCandidateBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length) - motivationReserve,
      fictionBookIds
    );
    const motivationSampleBookIds = this.takeUniqueBookIds(
      motivationBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length + nonFictionInterestBookIds.length),
      [...fictionBookIds, ...nonFictionInterestBookIds]
    );
    const nonFictionBookIds = [...nonFictionInterestBookIds, ...motivationSampleBookIds];
    const extraFictionBookIds = this.takeUniqueBookIds(
      prioritizedFictionBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length + nonFictionBookIds.length),
      [...fictionBookIds, ...nonFictionBookIds]
    );
    const displayedFictionBookIds = [...fictionBookIds, ...extraFictionBookIds];

    return [
      {
        title:
          fictionInterestIds.length > 0
            ? $localize`:@@kindleFictionSamplesSelected:🎭 Fiction picks from your shelf`
            : $localize`:@@kindleFictionSamplesDefault:🎭 Popular fiction samples`,
        tracks: this.sampleContentService.getBooks(displayedFictionBookIds),
      },
      {
        title:
          nonFictionInterestIds.length > 0 || motivationIds.length > 0
            ? $localize`:@@kindleNonFictionSamplesSelected:📚 Non-fiction picks from your shelf`
            : $localize`:@@kindleNonFictionSamplesDefault:📚 Popular non-fiction samples`,
        tracks: this.sampleContentService.getBooks(nonFictionBookIds),
      },
    ].filter(section => section.tracks.length > 0);
  }

  protected getBookIdsForInterests<T extends FictionInterestId | NonFictionInterestId>(
    interestIds: T[],
    map: Record<T, readonly BookId[]>,
    fallbackIds: readonly BookId[]
  ): BookId[] {
    const mappedBookIds = interleaveBookIds(interestIds.map(interestId => map[interestId] || []));

    return mappedBookIds.length > 0
      ? mappedBookIds
      : this.takeUniqueBookIds(fallbackIds, fallbackIds.length);
  }

  protected getMotivationBookIds(motivationIds: MotivationId[]): BookId[] {
    const result: BookId[] = [];

    motivationIds.forEach(motivationId =>
      this.addBookId(result, motivationBookIdMap[motivationId])
    );

    return result;
  }

  protected takeUniqueBookIds(
    bookIds: readonly BookId[],
    limit: number,
    excludedBookIds: readonly BookId[] = []
  ): BookId[] {
    const result: BookId[] = [];
    const excluded = new Set(excludedBookIds);

    for (const bookId of bookIds) {
      if (result.length >= limit) {
        break;
      }

      if (!excluded.has(bookId) && !result.includes(bookId)) {
        result.push(bookId);
      }
    }

    return result;
  }

  protected addBookId(target: BookId[], bookId: BookId | undefined): void {
    if (bookId && !target.includes(bookId)) {
      target.push(bookId);
    }
  }

  protected getRemainingSampleLimit(selectedCount: number): number {
    return Math.max(0, maxKindlePaywallSamples - selectedCount);
  }

  override getFaqs(): PaywallFaq[] {
    if (this.isOnMainFlow()) {
      return super.getFaqs();
    }

    return [
      {
        question: $localize`:@@kindleFaqUseKindleQuestion:Can I use Peech for books from my Kindle library?`,
        answer: $localize`:@@kindleFaqUseKindleAnswer:Peech turns readable text and supported files you provide into audio. Some Kindle purchases are protected by publisher restrictions, so availability depends on whether you can access the text in a supported format.`,
      },
      {
        question: $localize`:@@kindleFaqAudibleQuestion:Is this the same as Audible?`,
        answer: $localize`:@@kindleFaqAudibleAnswer:No. Audible sells finished audiobooks. Peech helps you listen to text you already have, including supported ebooks, documents, links, notes, and long reads.`,
      },
      {
        question: $localize`:@@kindleFaqLongChaptersQuestion:Does it work for long chapters?`,
        answer: $localize`:@@kindleFaqLongChaptersAnswer:Yes. Peech is built for long-form listening, with natural voices, playback speed control, and a simple player so you can keep your place as you move through a book.`,
      },
      {
        question: $localize`:@@kindleFaqVoiceSpeedQuestion:Can I change the voice and speed?`,
        answer: $localize`:@@kindleFaqVoiceSpeedAnswer:Yes. You can choose voices and adjust playback speed, so fiction can feel more expressive and non-fiction can move at the pace you prefer.`,
      },
      {
        question: $localize`:@@faqGetAccess:How do I get access to the app?`,
        answer: $localize`:@@faqGetAccessAnswer:After you fill in your email address, a mail with app download link will be in your inbox. Just install the app, Sign In with your email.`,
      },
      {
        question: $localize`:@@faqCancelAccount:How do I cancel my account?`,
        answer: $localize`:@@faqCancelAccountAnswer:It's easy! Just log into your account here, and click the Cancel button.`,
      },
    ];
  }

  override getSocialProofText(totalUsersThisHour: number): string {
    if (this.isOnMainFlow()) {
      return super.getSocialProofText(totalUsersThisHour);
    }

    return $localize`:@@kindleReadersStartedLastHour:${totalUsersThisHour}:INTERPOLATION: readers started a listening plan in the last hour`;
  }

  override getCommunityTitle(): string {
    if (this.isOnMainFlow()) {
      return super.getCommunityTitle();
    }

    return (
      $localize`:@@kindleCommunityTitlePrefix:Join readers ` +
      highlightText($localize`:@@kindleCommunityTitleHighlight:finishing more books`) +
      $localize`:@@kindleCommunityTitleSuffix: with Peech`
    );
  }
}
